/* eslint-disable @angular-eslint/prefer-standalone */
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  Renderer2,
  SecurityContext,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'g99-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  standalone: false,
})
export class ButtonComponent implements OnChanges, AfterViewInit {
  @Output() public buttonClick: EventEmitter<Event> = new EventEmitter<Event>();

  @Input() public loaderSize = 20;
  @Input() public loadingText = 'Saving...';
  @Input() public isLoading = false;
  @Input() public variant: 'small' | 'medium' | 'large' | 'custom' = 'medium';
  @Input() public color:
    | 'secondary'
    | 'danger'
    | 'danger-light'
    | 'danger-outline'
    | 'transparent'
    | 'transparent-with-border'
    | 'light'
    | 'light-green'
    | 'light-greyish'
    | 'white'
    | 'green'
    | 'info' = 'green';
  @Input() public disabled = false;
  @Input() public showIconOnMobile = false; // New flag for mobile icons

  @Input() public svgIcon: string | null = null; // HTML string for icon
  @Input() public icon: string | null = null; // HTML string for icon
  @Input() public iconPosition: 'left' | 'right' = 'left';
  @Input() public eventName = 'buttonClick'; // Default event name

  // New inputs for custom styles
  @Input() public customTextColor = '#000'; // Default text color
  @Input() public customBgColor = '#fff'; // Default background color
  @Input() public iconColor = '#fff'; // Default icon color
  @Input() public iconColorStrategy: 'fill' | 'stroke' | 'both' | 'none' =
    'fill';
  @Input() public iconFontSize = '16px'; // Default icon font size
  @Input() public iconHeight = 14; // Default height
  @Input() public iconWidth = 14; // Default width

  @Input() public isShowIconLoader = false; // Default icon loader

  @Input() public height = '40px';
  @Input() public width = '90px';
  @Input() public border = 'solid';
  @Input() public borderColor = '#E9EBED';
  @Input() public borderWidth = '0px';
  @Input() public borderRadius = '';
  @Input() public fontSize = '14px';
  @Input() public fontWeight = '500';
  @Input() public className = '';
  @Input() public padding = '';
  @ViewChild('svgContainer', { static: false })
  public svgContainer!: ElementRef;

  public safeSvgIcon!: SafeHtml;

  constructor(
    private readonly _renderer: Renderer2,
    private readonly _elRef: ElementRef,
    private readonly _sanitizer: DomSanitizer
  ) {}

  ngAfterViewInit(): void {
    if (this.svgContainer) {
      const container = this.svgContainer.nativeElement;
      this._renderer.setProperty(container, 'innerHTML', this.svgIcon);

      // Find the SVG element within the container
      const svgElement = container.querySelector('svg');
      if (svgElement) {
        // Apply the iconColor to the SVG and its child paths
        this._renderer.setStyle(svgElement, 'color', this.iconColor); // General color
        this._renderer.setAttribute(svgElement, 'fill', this.iconColor); // Apply fill to SVG
        this.applyColorToPaths(svgElement);
      }
    }
  }

  public applyColorToPaths(svgElement: SVGElement): void {
    // Query all path elements within the SVG
    const paths = svgElement.querySelectorAll('path');
    paths.forEach((path) => {
      this._renderer.setAttribute(path, 'fill', this.iconColor); // Apply color to paths
    });
  }

  // Handle click event
  public handleClick(event: Event): void {
    if (!this.disabled) {
      this.buttonClick.emit(event);
    }
  }

  // Get custom styles for the button
  public getCustomStyles(): { [key: string]: string } {
    let styles: { [key: string]: string } = {};

    if (this.variant === 'custom') {
      styles = {
        color: this.customTextColor,
        'background-color': this.customBgColor,
        height: this.height,
        width: this.width,
        border: this.border,
        borderColor: this.borderColor,
        borderWidth: this.borderWidth || '1px',
        fontSize: this.fontSize,
        fontWeight: this.fontWeight,
        borderRadius: this.borderRadius,
      };
    } else {
      if (this.width) {
        styles['minWidth'] = this.width;
      }

      if (this.padding) {
        styles['padding'] = this.padding;
      }
      if (this.borderRadius) {
        styles['border-radius'] = this.borderRadius;
      }
    }

    return styles;
  }

  public ngOnChanges(): void {
    if (this.svgIcon) {
      const sanitized =
        this._sanitizer.sanitize(SecurityContext.HTML, this.svgIcon) || '';
      this.safeSvgIcon = this._sanitizer.bypassSecurityTrustHtml(sanitized);
    }
  }
}
